import NewAppointmentCard from 'components/cards/AppointmentCard/NewAppointmentCard'
import {
	useLocation,
	useParams,
} from 'react-router-dom/cjs/react-router-dom.min'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
	controllerFilters,
	currentDetailLevelState,
	currentUserProcessPreferences,
	expandedInboxWithSizeMediumState,
	searchProcessString,
} from 'store/atoms'
import { useFetchList } from 'store'
import styles from './ClinicalProcess.module.sass'
import { Appointment, Requisition, ProcessChangesHistory } from 'models'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import NoData from 'components/general/NoData/NoData'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import { useEffect, useMemo, useRef, useState } from 'react'
import AppointmentHelpTooltip from 'components/cards/AppointmentCard/AppointmentHelpTooltip/AppointmentHelpTooltip'
import useTermSearch from 'hooks/useTermSearch'
import { useDeepCompareEffect } from 'react-use'
import { getScrollParentOffsetY } from '@jimmyboys/liita-ui/dist/utils/getScrollParent'

const ClinicalAppointmentsController = () => {
	const { i18n, screenSize } = useGlobal()
	const { clinicalProcessId } = useParams()
	const collectionRef = useRef(null)
	const expandedInboxMedium = useRecoilValue(expandedInboxWithSizeMediumState)
	const [showHelp, setShowHelp] = useState(false)
	const [removeBackdrop, setRemoveBackdrop] = useState(false)
	const detailLevel = useRecoilValue(currentDetailLevelState)
	const processString = useRecoilValue(searchProcessString)
	const [appointmentFilters, setAppointmentFilters] =
		useRecoilState(controllerFilters)

	const [, scrollTo] = getScrollParentOffsetY(collectionRef.current)

	const location = useLocation()
	const anchorTag = location.hash.trim().split('_')?.[1]

	const processPreferences = useRecoilValue(currentUserProcessPreferences)

	const appointmentsUri = `/clinical_processes/${clinicalProcessId}/appointments`
	const requisitionsUri = `/clinical_processes/${clinicalProcessId}/requisitions`
	const processChangesHistoryUri = `/clinical_processes/${clinicalProcessId}/history`

	const toggleHelp = () => setShowHelp((value) => !value)
	const toggleBackdrop = () => setRemoveBackdrop((value) => !value)

	const backdropRef = useRef()

	const cardLabels = {
		goToAppointment: i18n`Go to appointment`,
		doctor: i18n`Doctor`,
		diagnosis: i18n`Diagnosis`,
		disability: i18n`Disability`,
		dateAndPlace: i18n`Date/Place`,
		outsideNetwork: i18n`outside network`,
		place: i18n`Place`,
		medicalObservation: i18n`Medical Observation`,
		documents: i18n`Documents`,
		current: i18n`Current`,
		causalLink: i18n`Causal Link`,

		comments: i18n`Comments`,

		goToRequisition: i18n`Go to requisition`,
		requisition: i18n`Requisition`,
		execution: i18n`Execution`,
		applicant: i18n`Applicant`,
		type: i18n`Type`,
		medicalActs: i18n`Medical Acts`,
		clinicalInformation: i18n`Clinical Information`,
		exams: i18n`Exams`,
		notDefined: i18n`Not defined`,
		changesHistory: i18n`Changes History`,
		requestedBy: i18n`Requested by`,
		toTheDateOf: i18n`to the date of`,

		seeMore: i18n`See more`,
		seeLess: i18n`See less`,
		cancelMotive: i18n`Cancel motive`,
		nonExecutionMotive: i18n`Non-execution motive`,
		withoutObservations: i18n`Without observations`,
		unscheduledAppointment: i18n`Unscheduled appointment`,

		requisitions: i18n`Requisitions`,
		history: i18n`History`,

		nullify: i18n`Nullify`,
		nullifyAppointment: i18n`Nullify Appointment`,
		nullifyMessagePart1: i18n`By nullify this appointment,`,
		nullifyMessagePart2Singular: i18n`requisition will be nullified.`,
		nullifyMessagePart2Plural: i18n`requisitions will be nullified.`,
		nullifyMessagePart3: i18n`If any appointment derive from this, will also be nullified.`,
		historyChanges: i18n`Changes history`,
		historyAppointment: i18n`Changes appointment`,
	}

	const filterCancelledAppointments = true

	const { contents, state } = useFetchList(
		[appointmentsUri, requisitionsUri, processChangesHistoryUri],
		[
			Appointment.fromJSON,
			Requisition.fromJSON,
			ProcessChangesHistory.fromJSON,
		]
	)

	const isLoading = state === 'loading'

	const [appointmentsData, requisitionsData, processChangesHistory = []] =
		contents || []

	let appointments = appointmentsData || []
	if (detailLevel === 'summary') {
		appointments = appointments.filter(
			(elem) => elem.status.value !== 'cancelled'
		)
	}

	processChangesHistory?.sort(
		(a, b) =>
			new Date(b.modificationDate.timestamp) -
			new Date(a.modificationDate.timestamp)
	)

	let groupedHistory = { appointments: [], requisitions: [] }
	groupedHistory = (processChangesHistory || []).reduce(
		(acc, elem) => {
			if (elem.requisitionId) {
				if (acc['requisitions'][elem.requisitionId] === undefined) {
					acc['requisitions'][elem.requisitionId] = []
				}
				acc['requisitions'][elem.requisitionId].push(elem)
			} else if (elem.appointmentId) {
				if (acc['appointments'][elem.appointmentId] === undefined) {
					acc['appointments'][elem.appointmentId] = []
				}
				acc['appointments'][elem.appointmentId].push(elem)
			}

			return acc
		},
		{ appointments: [], requisitions: [] }
	)

	appointments = appointments
		.map((json) => {
			return {
				...json,
				processId: clinicalProcessId,
				history: groupedHistory['appointments']?.[json.id],
				sortParam: json.date.timestamp,
				$cardLabels: cardLabels,
				$displayAnchorLink: true,
				$detailLevel: detailLevel,
				$highlight: json.id === anchorTag,
			}
		})
		.filter(
			({ status }) =>
				!(filterCancelledAppointments && status?.value === 'CANCELLED')
		)

	const data = [...appointments].map((item) => {
		return {
			...item,
			requisitions: requisitionsData
				.filter((requisition) => requisition.appointmentId === item.id)
				.map((elem) => ({
					...elem,
					history: groupedHistory['requisitions']?.[elem.id],
					sortParam:
						elem.executionDate?.timestamp ||
						elem.scheduledDate?.timestamp ||
						elem.requisitionDate?.timestamp,
					$cardLabels: cardLabels,
					$displayAnchorLink: true,
					$detailLevel: detailLevel,
				}))
				.sort((a, b) => b.sortParam - a.sortParam),
		}
	})

	const { searchMatches, setQuery } = useTermSearch(data, [
		'medicalObservation',
		'id',
	])

	useEffect(() => {
		setQuery(processString)
	}, [processString, setQuery])

	useEffect(() => {
		setAppointmentFilters((prev) => {
			return {
				...prev,
				appointments: {
					filters: { status: [], provider: [], specialty: [] },
					filterValuesList: [],
				},
			}
		})
	}, [clinicalProcessId, setAppointmentFilters])

	const providers = useMemo(() => {
		return data?.length
			? [
					{ value: '', text: i18n`Any` },
					...[...new Set(data.map((ele) => ele.establishment?.name))]
						.filter(Boolean)
						.map((ele) => ({
							value: ele,
							text: i18n.translate(ele),
						})),
			  ]
			: [{ value: '', text: i18n`Any` }]
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data])

	const specialties = useMemo(() => {
		return data?.length
			? [
					{ value: '', text: i18n`Any` },
					...[
						...new Set(
							data.map((ele) => ele.medicalSpecialty?.name)
						),
					]
						.filter(Boolean)
						.map((ele) => ({
							value: ele,
							text: i18n.translate(ele),
						})),
			  ]
			: [{ value: '', text: i18n`Any` }]
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data])

	const status = useMemo(() => {
		return data?.length
			? [
					...[...new Set(data.map((ele) => ele.status?.description))]
						.filter(Boolean)
						.map((ele) => ({
							value: ele,
							text: ele,
						})),
			  ]
			: []
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data])

	const { filters } = appointmentFilters['appointments']

	const [providerValuesList, specialtyValuesList, statusValuesList] =
		useMemo(() => {
			const providerValuesList = filters?.provider?.length
				? providers.map((elem) => {
						return filters?.provider?.includes(elem.value)
							? { ...elem, $isSelected: true }
							: elem
				  })
				: [
						{ ...providers[0], $isSelected: true },
						...providers.slice(1),
				  ]

			const specialtyValuesList = filters?.specialty?.length
				? specialties.map((elem) => {
						return filters?.specialty?.includes(elem.value)
							? { ...elem, $isSelected: true }
							: elem
				  })
				: [
						{ ...specialties[0], $isSelected: true },
						...specialties.slice(1),
				  ]

			const statusValuesList = filters?.status?.length
				? status.map((elem) => {
						return filters?.status?.includes(elem.value)
							? { ...elem, $isSelected: true }
							: elem
				  })
				: [...status]

			return [providerValuesList, specialtyValuesList, statusValuesList]
		}, [
			filters?.provider,
			filters?.specialty,
			filters?.status,
			providers,
			specialties,
			status,
		])

	useDeepCompareEffect(() => {
		setAppointmentFilters((prev) => {
			return {
				...prev,
				appointments: {
					...prev['appointments'],
					filterValuesList: [
						providerValuesList,
						specialtyValuesList,
						statusValuesList,
					],
				},
			}
		})
	}, [
		appointmentFilters,
		providerValuesList,
		setAppointmentFilters,
		specialtyValuesList,
	])

	let finalCollection = useMemo(() => {
		if (searchMatches.length) {
			let value = searchMatches
			if (filters?.provider?.length) {
				value = value.filter((elem) => {
					return filters?.provider.includes(elem?.establishment?.name)
				})
			}

			if (filters?.specialty?.length) {
				value = value.filter((elem) => {
					return filters?.specialty.includes(
						elem?.medicalSpecialty?.name
					)
				})
			}

			if (filters?.status?.length) {
				value = value.filter((elem) => {
					return filters?.status.includes(elem?.status?.description)
				})
			}

			return value
		}
	}, [filters?.provider, filters?.specialty, filters?.status, searchMatches])

	if (finalCollection) {
		if (processPreferences?.order) {
			finalCollection = [...finalCollection].sort((a, b) => {
				if (!b.scheduled) {
					return Number.MIN_SAFE_INTEGER
				}

				return a.date.timestamp - b.date.timestamp
			})
		} else {
			finalCollection = [...finalCollection].sort((a, b) => {
				if (!b.scheduled) {
					return Number.MIN_SAFE_INTEGER
				}

				return b.date.timestamp - a.date.timestamp
			})
		}
	}

	useEffect(() => {
		if (anchorTag && finalCollection?.length) {
			const selectedAppointment = document.getElementById(anchorTag)
			const appointmentPos = selectedAppointment?.getBoundingClientRect()

			if (appointmentPos) {
				let itemPos = appointmentPos.top

				scrollTo &&
					scrollTo(
						screenSize === 'small' ? itemPos : itemPos - 100,
						0,
						'smooth'
					)
			}
		}
	}, [screenSize, finalCollection, anchorTag, scrollTo])

	return (
		<>
			{!searchMatches.length ? (
				<NoData
					applyMaxWidth
					paddings={['normal', 'normal']}
					message={
						isLoading
							? i18n`Loading`
							: i18n`This process has no extra data to show.`
					}
				/>
			) : (
				<div ref={collectionRef}>
					{!expandedInboxMedium && screenSize !== 'small' ? (
						<div
							className={styles['appointment-controller-header']}
						>
							<div
								className={
									styles[
										'appointment-controller-header-label1'
									]
								}
							>
								<h5>{i18n`Appointment Data`}</h5>
							</div>
							<div
								className={
									styles[
										'appointment-controller-header-label2'
									]
								}
							>
								<h5>{i18n`Medical Observation`}</h5>
							</div>
							<div
								className={
									styles['appointment-controller-header-help']
								}
							>
								<button
									className={styles['help-button']}
									onClick={() => {
										toggleHelp()
										toggleBackdrop()
									}}
									ref={backdropRef}
								>
									<Icon
										className={
											styles[
												'appointment-controller-header-icon'
											]
										}
										name='help'
										width='20px'
										height='20px'
									/>
								</button>
								<AppointmentHelpTooltip
									showHelp={showHelp}
									removeBackdrop={removeBackdrop}
									toggleHelp={toggleHelp}
									toggleBackdrop={toggleBackdrop}
									ref={backdropRef}
								/>
							</div>
						</div>
					) : null}
					{finalCollection.map((item) => (
						<NewAppointmentCard
							id={item.id}
							key={item.id}
							data={item}
						/>
					))}
				</div>
			)}
		</>
	)
}
export default ClinicalAppointmentsController
